import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import clsx from "clsx";

export type ToastKind = "note" | "success" | "caution" | "problem";

export type ToastAction = {
  label: string;
  onSelect: () => void;
};

export type Toast = {
  id: string;
  kind: ToastKind;
  title: string;
  body?: string;
  action?: ToastAction;
  /** Milliseconds; 0 keeps it until dismissed. */
  duration: number;
};

export type ToastInput = {
  kind?: ToastKind;
  title: string;
  body?: string;
  action?: ToastAction;
  duration?: number;
};

type ToastContextValue = {
  toasts: Toast[];
  push: (toast: ToastInput) => string;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function newId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `toast-${Math.random().toString(36).slice(2)}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const toast: Toast = {
        id: newId(),
        kind: input.kind ?? "note",
        title: input.title,
        duration: input.duration ?? (input.kind === "problem" ? 9000 : 5500),
        ...(input.body !== undefined ? { body: input.body } : {}),
        ...(input.action !== undefined ? { action: input.action } : {}),
      };
      setToasts((current) => [...current.slice(-3), toast]);
      if (toast.duration > 0) {
        const timer = window.setTimeout(() => dismiss(toast.id), toast.duration);
        timers.current.set(toast.id, timer);
      }
      return toast.id;
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToasts(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToasts must be used inside a ToastProvider");
  return context;
}

const KIND_STYLES: Record<ToastKind, { rail: string; label: string; text: string }> = {
  note: { rail: "bg-ink-soft", label: "Note", text: "text-ink" },
  success: { rail: "bg-moss", label: "Filed", text: "text-ink" },
  caution: { rail: "bg-ochre", label: "Take care", text: "text-ink" },
  problem: { rail: "bg-rust", label: "Trouble", text: "text-ink" },
};

/**
 * The toast viewport. Sits above the thumb bar on mobile, bottom-right on
 * larger screens. Announced politely, never stealing focus.
 */
export function Toasts() {
  const { toasts, dismiss } = useToasts();

  return (
    <div
      aria-live="polite"
      aria-relevant="additions text"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:items-end sm:px-6 sm:pb-6"
    >
      {toasts.map((toast) => {
        const styles = KIND_STYLES[toast.kind];
        return (
          <div
            key={toast.id}
            role="status"
            className="pointer-events-auto flex w-full max-w-sm animate-rise overflow-hidden rounded-[2px] border border-rule bg-card shadow-lifted"
          >
            <div className={clsx("w-1 shrink-0", styles.rail)} aria-hidden="true" />
            <div className="flex min-w-0 flex-1 items-start gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className={clsx("field-label-tight", styles.text)}>{styles.label}</p>
                <p className="mt-1 font-prose text-[0.95rem] leading-snug text-ink">{toast.title}</p>
                {toast.body ? (
                  <p className="mt-1 font-prose text-sm leading-snug text-ink-soft">{toast.body}</p>
                ) : null}
                {toast.action ? (
                  <button
                    type="button"
                    onClick={() => {
                      toast.action?.onSelect();
                      dismiss(toast.id);
                    }}
                    className="link mt-2 font-label text-sm"
                  >
                    {toast.action.label}
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss"
                className="touch -m-2 flex items-center justify-center text-ink-soft transition-colors hover:text-ink"
              >
                <span aria-hidden="true" className="text-lg leading-none">
                  ×
                </span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
